import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../styles.css';

const Home = () => {
    const { isAdmin } = useAuth();
    const [hero, setHero] = React.useState({
        main: null,
        featured: [null, null, null]
    });
    const [heroText, setHeroText] = React.useState({
        title: 'Tzofit Ofengenden',
        subtitle: 'Associate professor of Ethics and Bioethics and contemporary painter. Exploring color, texture, and form to expand perception and invite contemplation.',
        metaLeft: 'New Orleans, LA',
        metaRight: 'Abstract & Contemporary'
    });

    React.useEffect(() => {
        const unsub = onSnapshot(collection(db, 'homeImages'), (snap) => {
            const data = { main: null, featured: [null, null, null] };
            snap.forEach(d => {
                const v = d.data();
                if (d.id === 'main' && v?.src) data.main = v.src;
                if (d.id.startsWith('featured-') && v?.src) {
                    const idx = Number(d.id.split('-')[1]);
                    if (!Number.isNaN(idx)) data.featured[idx] = v.src;
                }
            });
            setHero(data);
        });
        return () => unsub();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const unsub = onSnapshot(doc(db, 'siteContent', 'home'), (snap) => {
            const data = snap.data();
            if (data) setHeroText((t) => ({ ...t, ...data }));
        });
        return () => unsub();
    }, []);

    const handleReplace = async (key) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/jpeg,image/png,image/webp';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const path = `home/${key}/${file.name}`;
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            await setDoc(doc(db, 'homeImages', key), { src: url });
        };
        input.click();
    };

    return (
        <section id="home" className="section">
            <div className="container home-hero">
                {hero.main ? (
                    <div className="image-blend-wrap hero" style={{ '--bg': `url(${hero.main})` }}>
                        <img
                            src={hero.main}
                            alt="Abstract painting by Tzofit Ofengenden"
                            className="hero-image contain"
                            loading="eager"
                        />
                    </div>
                ) : (
                    <div className="hero-image-skeleton" aria-hidden="true" />
                )}

                <div className="hero-content">
                    <h1>{heroText.title}</h1>
                    <p>{heroText.subtitle}</p>
                    <div className="hero-meta">
                        <span>{heroText.metaLeft}</span>
                        <span>—</span>
                        <span>{heroText.metaRight}</span>
                    </div>
                    <div className="hero-actions">
                        <Link to="/gallery" className="exhibition-link">View Gallery</Link>
                        <Link to="/about" className="btn-outline">About</Link>
                        {isAdmin && (
                            <button type="button" className="btn-outline" onClick={() => handleReplace('main')}>Replace Main Image</button>
                        )}
                    </div>
                    {isAdmin && (
                        <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                            <input type="text" value={heroText.title} onChange={(e) => setHeroText({ ...heroText, title: e.target.value })} />
                            <textarea rows={3} value={heroText.subtitle} onChange={(e) => setHeroText({ ...heroText, subtitle: e.target.value })} />
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input type="text" value={heroText.metaLeft} onChange={(e) => setHeroText({ ...heroText, metaLeft: e.target.value })} />
                                <input type="text" value={heroText.metaRight} onChange={(e) => setHeroText({ ...heroText, metaRight: e.target.value })} />
                            </div>
                            <button type="button" className="btn-submit" onClick={async () => {
                                await setDoc(doc(db, 'siteContent', 'home'), heroText, { merge: true });
                            }}>Save Hero Text</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="container home-featured">
                <h3>Featured Works</h3>
                <div className="home-featured-grid">
                    {hero.featured.map((src, idx) => (
                        <div className="home-featured-card" key={idx}>
                            {src ? (
                                <div className="image-blend-wrap featured" style={{ '--bg': `url(${src})` }}>
                                    <img src={src} alt={`Featured ${idx+1}`} loading="lazy" className="contain" />
                                </div>
                            ) : (
                                <div className="featured-skeleton" aria-hidden="true" />
                            )}
                            {isAdmin && (
                                <div style={{ marginTop: 8 }}>
                                    <button type="button" className="btn-outline" onClick={() => handleReplace(`featured-${idx}`)}>Replace</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Home;